import { defineStore } from "pinia";
// Type imports
import type {
	Application,
	Currency
} from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type {
	Account,
	MenuLink,
	ResolvedMenuNode,
	Search,
	SiteLogo,
	TopNavbarLeft,
	ApplicationFlag,
	TopBarItem,
	StockStatusItem,
	CartRecommendationsBlok
} from "~/types/storyblok/storyblok";
import type { CulturesNorce } from "~/types/components/header";
import type { ExtractedFooterFields } from "~/types/components/footer";

interface SiteLogoDimensions {
	width: number;
	height: number;
}

interface State {
	siteLogo: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	search: Search[];
	account: Account[];
	topNavbarLeft: TopNavbarLeft[];
	// Menu Node menu from /api/menu; empty until the space has one, and then
	// the old menuCategories / menuCategoriesNorce below are shown instead.
	menu: ResolvedMenuNode[];
	menuCategories: MenuLink[];
	menuCategoriesNorce: Category[];
	applicationNorce: Application | null;
	culturesNorce: CulturesNorce[];
	currenciesNorce: Currency[];
	headerLoaded: boolean;
	flags: ApplicationFlag[];
	topBarItems: TopBarItem[] | null;
	customerServicePhone: string | null;
	stockStatus: StockStatusItem[];
	footer: ExtractedFooterFields | null;
	/** `layouts/main` → `cart_recommendations`. Empty = the feature is off. */
	cartRecommendations: CartRecommendationsBlok | null;
}

export const useAppStore = defineStore("appStore", {
	state: (): State => ({
		siteLogo: undefined,
		siteLogoDimensions: {
			width: 200,
			height: 70
		},
		search: [],
		account: [],
		topNavbarLeft: [],
		menu: [],
		menuCategories: [],
		menuCategoriesNorce: [],
		applicationNorce: null,
		culturesNorce: [],
		currenciesNorce: [],
		headerLoaded: false,
		flags: [],
		topBarItems: null,
		customerServicePhone: null,
		stockStatus: [],
		footer: null,
		cartRecommendations: null
	}),

	getters: {
		getAppStore: (state) => state
	},

	actions: {
		initState(data: Partial<State>) {
			this.$patch((state) => {
				if (data.siteLogo) state.siteLogo = data.siteLogo;
				if (data.siteLogoDimensions) {
					state.siteLogoDimensions = {
						...state.siteLogoDimensions,
						...data.siteLogoDimensions
					};
				}
				if (data.search) state.search = data.search;
				if (data.account) state.account = data.account;
				if (data.topNavbarLeft) state.topNavbarLeft = data.topNavbarLeft;
				if (data.topBarItems) state.topBarItems = data.topBarItems;
				if (data.menu) state.menu = data.menu;
				if (data.menuCategories) state.menuCategories = data.menuCategories;
				if (data.menuCategoriesNorce)
					state.menuCategoriesNorce = data.menuCategoriesNorce;
				if (data.applicationNorce)
					state.applicationNorce = data.applicationNorce;
				if (data.culturesNorce) state.culturesNorce = data.culturesNorce;
				if (data.currenciesNorce) state.currenciesNorce = data.currenciesNorce;
				if (data.headerLoaded) state.headerLoaded = data.headerLoaded;
				if (data.flags) state.flags = data.flags;
				if (data.customerServicePhone)
					state.customerServicePhone = data.customerServicePhone;
				if (data.stockStatus) state.stockStatus = data.stockStatus;
				if (data.footer) state.footer = data.footer;
				if (data.cartRecommendations)
					state.cartRecommendations = data.cartRecommendations;
			});
		}
	}
});
